// src/hooks/useInventoryIntel.js
// --- UPDATE (Inventory Enterprise v1) — NEW FILE -----------------------------------------
// Live inventory + fleet intelligence: GET /inventory-intel/overview, refreshed when the
// WebSocket reports inventory_update (stock movements, links, vehicles, item edits) or work-order
// completions (parts get used), coalesced into one refetch; polling fallback every 2 min.
// --- END UPDATE ---------------------------------------------------------------------------
import { useCallback, useEffect, useRef, useState } from 'react';
import webSocketService from '../services/websocketService';
import inventoryIntelApi from '../services/inventoryIntelApi';

export const INVENTORY_EVENTS = Object.freeze(['inventory_update', 'work_order_completed', 'low_stock_alert']);
const DEBOUNCE_MS = 1200;

export default function useInventoryIntel({ enabled = true } = {}) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [lastEvent, setLastEvent] = useState(null);
    const mounted = useRef(true);
    const abortRef = useRef(null);
    const timer = useRef(null);

    const load = useCallback(async ({ refresh = false, quiet = false } = {}) => {
        if (!enabled) return;
        abortRef.current?.abort?.();
        const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        abortRef.current = ctrl;
        if (!quiet) setIsLoading(true);
        try {
            const o = await inventoryIntelApi.overview({ refresh, signal: ctrl?.signal });
            if (!mounted.current || abortRef.current !== ctrl) return;
            setData(o); setError(null); setUpdatedAt(new Date());
        } catch (e) {
            if (e?.name === 'AbortError' || !mounted.current) return;
            setError(e.status === 404 ? 'Inventory intelligence is not on this server yet — deploy routes/inventoryIntelRoutes.js and restart.'
                : e.status === 403 ? 'Your role cannot view inventory intelligence.' : e.message || 'Could not load inventory intelligence.');
        } finally { if (mounted.current && abortRef.current === ctrl) setIsLoading(false); }
    }, [enabled]);

    useEffect(() => {
        mounted.current = true;
        load();
        return () => { mounted.current = false; abortRef.current?.abort?.(); clearTimeout(timer.current); };
    }, [load]);

    useEffect(() => {
        if (!enabled) return undefined;
        const onEvent = (msg) => {
            if (!mounted.current) return;
            const p = (msg && (msg.payload || msg.data)) || msg || {};
            setLastEvent({ type: msg?.type || 'inventory_update', reason: p.reason || null, at: new Date() });
            clearTimeout(timer.current);
            timer.current = setTimeout(() => { if (mounted.current) load({ quiet: true }); }, DEBOUNCE_MS);
        };
        INVENTORY_EVENTS.forEach((e) => webSocketService.subscribe(e, onEvent));
        const poll = setInterval(() => { if (typeof document === 'undefined' || !document.hidden) load({ quiet: true }); }, 120000);
        return () => { clearInterval(poll); INVENTORY_EVENTS.forEach((e) => webSocketService.unsubscribe(e, onEvent)); };
    }, [enabled, load]);

    return { data, isLoading, error, updatedAt, lastEvent, reload: load, refresh: useCallback(() => load({ refresh: true }), [load]) };
}
